'use client';
import type { CSSProperties } from 'react';
import { CLASSNAME, getCentellian24CreditPercentage } from '@skin-seoul/service';
import { cn } from '@skin-seoul/design-system';

import { useProductCard } from '../ProductCardContext';
import { CreditSealCoinsIcon } from '../assets/CreditSealCoinsIcon';
import { CREDIT_SEAL_STAR_VIEWBOX_SIZE, CreditSealStar } from '../assets/CreditSealStar';
import {
  CREDIT_SEAL_BADGE_SPEC,
  creditSealStarOffset,
  creditSealStrokeInViewBox,
  type CreditSealBadgeVariant,
} from './creditSealBadgeVariants';

type CreditSealBadgeContentProps = {
  percentage: number;
  variant: CreditSealBadgeVariant;
  className?: string;
  /** Positioning only — the seal's own box is fixed by its variant. */
  style?: CSSProperties;
};

/**
 * Centellian24 extra-credit seal (SSD-6088), Figma `5775:81038`.
 *
 * Sits over the top-right of a product image; the discount badge keeps the
 * top-left corner, so the two never collide. Both text rows are absolutely
 * positioned at their Figma offsets and centred horizontally — stacking them
 * would misplace the percentage on M and S, whose rows overlap by 4px.
 */
export const CreditSealBadgeContent = ({ percentage, variant, className, style }: CreditSealBadgeContentProps) => {
  const spec = CREDIT_SEAL_BADGE_SPEC[variant];
  const starOffset = creditSealStarOffset(spec);

  return (
    <div
      className={cn('relative shrink-0 text-generic-0', CLASSNAME.NOT_TRANSLATE, className)}
      style={{ ...style, width: spec.size, height: spec.size }}
      data-testid="credit-seal-badge"
      data-credit-seal-variant={variant}
    >
      <span
        className="absolute block"
        style={{
          top: starOffset,
          left: starOffset,
          width: spec.starSize,
          height: spec.starSize,
        }}
      >
        <CreditSealStar
          strokeWidth={creditSealStrokeInViewBox(spec, CREDIT_SEAL_STAR_VIEWBOX_SIZE)}
          className="block h-full w-full"
        />
      </span>
      <div
        className="absolute flex items-center justify-center"
        style={{ top: spec.labelTopPx, left: 0, right: 0, gap: spec.iconLabelGap }}
      >
        <CreditSealCoinsIcon size={spec.iconSize} className="shrink-0" />
        <span
          className="whitespace-nowrap font-medium"
          style={{
            fontSize: spec.labelFontSize,
            lineHeight: `${spec.labelLineHeight}px`,
            letterSpacing: spec.labelLetterSpacing,
          }}
        >
          Credit
        </span>
      </div>
      <span
        className="absolute block whitespace-nowrap text-center font-semibold"
        style={{
          top: spec.percentageTopPx,
          left: 0,
          right: 0,
          fontSize: spec.percentageFontSize,
          lineHeight: `${spec.percentageLineHeight}px`,
        }}
      >
        {percentage}%
      </span>
    </div>
  );
};

type CreditSealBadgeProps = {
  variant: CreditSealBadgeVariant;
  className?: string;
  /** Positioning only — the seal's own box is fixed by its variant. */
  style?: CSSProperties;
};

/** Product-card binding: reads the brand from context and hides itself off-campaign. */
export const CreditSealBadge = ({ variant, className, style }: CreditSealBadgeProps) => {
  const { brand, sku } = useProductCard();
  const percentage = getCentellian24CreditPercentage({ brand, sku });

  if (percentage <= 0) {
    return null;
  }

  return (
    <CreditSealBadgeContent percentage={percentage} variant={variant} className={className} style={style} />
  );
};
